import type { ModeConfig } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

interface InstructionsProps {
  config: ModeConfig;
  difficulty: number;
  highScore: number;
  instructions: string[];
  difficultyDescription?: string;
  onStart: () => void;
  onBack: () => void;
  /**
   * Optional — when present, renders +/− buttons next to the difficulty
   * label so testers can tune before starting. Callback is responsible
   * for clamping (useGameSession.setDifficulty already does this).
   */
  onDifficultyChange?: (next: number) => void;
}

export function Instructions({
  config,
  difficulty,
  highScore,
  instructions,
  difficultyDescription,
  onStart,
  onBack,
  onDifficultyChange,
}: InstructionsProps) {
  const { t } = useTranslation();
  const canTune = !!onDifficultyChange;
  const atMin = difficulty <= 1;
  const atMax = difficulty >= config.maxDifficulty;

  return (
    <main className="min-h-screen flex flex-col px-5 py-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <button
          type="button"
          onClick={onBack}
          className="font-mono text-xs tracking-[0.06em] text-fg-2 hover:text-fg-0"
        >
          ← {t("common.menu")}
        </button>
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-fg-3">
          {t("common.high")} {highScore}
        </span>
      </div>

      <h1 className="text-4xl md:text-5xl font-medium tracking-[-0.02em] mb-2">
        {t(`mode.${config.modeId}.name`)}
      </h1>
      <p className="text-fg-1 mb-10">{t(`mode.${config.modeId}.description`)}</p>

      <ul className="flex flex-col gap-3 mb-10">
        {instructions.map((line, i) => (
          <li
            key={i}
            className="flex gap-3 text-sm text-fg-1 leading-relaxed border-l-2 border-[var(--color-line-strong)] pl-4"
          >
            <span className="font-mono text-fg-3 text-xs leading-5">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>

      {canTune && (
        <div className="mb-8 border-t border-b border-[var(--color-line)] py-5 flex flex-col gap-2">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-fg-3">
            {t("instructions.difficulty")}
          </span>
          <div className="flex items-center gap-4">
            <button
              type="button"
              aria-label={t("instructions.decreaseDifficulty")}
              disabled={atMin}
              onClick={() => onDifficultyChange!(difficulty - 1)}
              className={[
                "w-11 h-11 rounded-full font-mono text-xl border transition-all active:scale-95",
                atMin
                  ? "bg-bg-2 border-[var(--color-line)] text-fg-4 opacity-40 cursor-not-allowed"
                  : "bg-bg-2 border-[var(--color-line-strong)] text-fg-0 hover:border-purple",
              ].join(" ")}
            >
              −
            </button>
            <span className="text-3xl font-medium tabular-nums w-12 text-center">
              {difficulty}
            </span>
            <button
              type="button"
              aria-label={t("instructions.increaseDifficulty")}
              disabled={atMax}
              onClick={() => onDifficultyChange!(difficulty + 1)}
              className={[
                "w-11 h-11 rounded-full font-mono text-xl border transition-all active:scale-95",
                atMax
                  ? "bg-bg-2 border-[var(--color-line)] text-fg-4 opacity-40 cursor-not-allowed"
                  : "bg-bg-2 border-[var(--color-line-strong)] text-fg-0 hover:border-purple",
              ].join(" ")}
            >
              +
            </button>
            <span className="font-mono text-[10px] tracking-[0.06em] text-fg-3 ml-auto">
              {t("instructions.max", { max: config.maxDifficulty })}
            </span>
          </div>
          {difficultyDescription && (
            <p className="text-xs font-mono text-fg-3 tracking-[0.04em] mt-1">
              {difficultyDescription}
            </p>
          )}
        </div>
      )}

      {!canTune && difficultyDescription && (
        <p className="text-xs font-mono text-fg-3 tracking-[0.04em] mb-8">
          {difficultyDescription}
        </p>
      )}

      <button
        type="button"
        onClick={onStart}
        className="self-start px-8 py-3 rounded-full text-base font-semibold transition-opacity hover:opacity-90"
        style={{ background: config.iconColor, color: "var(--color-bg-1)" }}
      >
        {t("common.start")}
      </button>
    </main>
  );
}
